import React, { useState, useCallback } from 'react';
import { Link2, Link2Off } from 'lucide-react';
import { NumberInput } from './NumberInput';
import { cn } from '../../../utils/cn';

interface Vector2InputProps {
  value: { x: number; y: number };
  onChange: (value: { x: number; y: number }) => void;
  labelX?: string;
  labelY?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  showLockRatio?: boolean;
  className?: string;
}

export const Vector2Input: React.FC<Vector2InputProps> = ({
  value,
  onChange,
  labelX = 'X',
  labelY = 'Y',
  min,
  max,
  step = 1,
  disabled = false,
  showLockRatio = false,
  className,
}) => {
  const [lockedRatio, setLockedRatio] = useState(false);
  const [ratio, setRatio] = useState(value.x !== 0 ? value.y / value.x : 1);

  const handleXChange = useCallback(
    (newX: number) => {
      if (lockedRatio && showLockRatio) {
        const newY = newX * ratio;
        onChange({ x: newX, y: newY });
      } else {
        onChange({ x: newX, y: value.y });
      }
    },
    [lockedRatio, showLockRatio, ratio, value.y, onChange]
  );

  const handleYChange = useCallback(
    (newY: number) => {
      if (lockedRatio && showLockRatio) {
        const newX = ratio !== 0 ? newY / ratio : value.x;
        onChange({ x: newX, y: newY });
      } else {
        onChange({ x: value.x, y: newY });
      }
    },
    [lockedRatio, showLockRatio, ratio, value.x, onChange]
  );

  const toggleLockRatio = useCallback(() => {
    if (!lockedRatio) {
      // Calculate and store the current ratio when locking
      setRatio(value.x !== 0 ? value.y / value.x : 1);
    }
    setLockedRatio(!lockedRatio);
  }, [lockedRatio, value.x, value.y]);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="flex-1">
        <NumberInput
          value={value.x}
          onChange={handleXChange}
          label={labelX}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
        />
      </div>
      <div className="flex-1">
        <NumberInput
          value={value.y}
          onChange={handleYChange}
          label={labelY}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
        />
      </div>
      {showLockRatio && (
        <button
          onClick={toggleLockRatio}
          disabled={disabled}
          className={cn(
            'p-1 rounded hover:bg-zinc-700 transition-colors',
            lockedRatio ? 'text-blue-400' : 'text-zinc-500',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          title={lockedRatio ? 'Unlock ratio' : 'Lock ratio'}
        >
          {lockedRatio ? <Link2 size={14} /> : <Link2Off size={14} />}
        </button>
      )}
    </div>
  );
};
